/**
 * Sources whose download failed (#891). A failure is taken to be a bot blocker, and nothing
 * tries to get past it: the admin opens each link, downloads the PDF in their own browser
 * and uploads it on the row. The source keeps its name, publisher and link.
 */
import React, { useState } from 'react';
import styled from '@emotion/styled';
import { ShieldAlert, Upload, ExternalLink } from 'lucide-react';
import { Button, StatusLine } from '../../../components/InstanceSettings/formPrimitives';
import { useTranslation } from '../../../hooks/useTranslation.jsx';
import { uploadSourceFile, needsManualDownload } from './datasetApi';
import { Section, SectionTitle, Lead, Meta } from './styles';

const Row = styled.li`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 16px;
  padding: 10px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  &:last-child { border-bottom: none; }
`;

const Name = styled.div`
  flex: 1 1 260px;
  min-width: 0;
  font-size: 13px;
  color: #ddd;
  a { color: #93c5fd; word-break: break-all; display: inline-flex; gap: 4px; align-items: center; }
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

function ManualRow({ datasetId, source, onChanged }) {
  const { t } = useTranslation();
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const handleUpload = async () => {
    setBusy(true);
    setError(null);
    try {
      await uploadSourceFile(datasetId, source.id, file);
      onChanged();
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <Row>
      <Name>
        <div>{source.title || source.link}</div>
        {source.publisher && <Meta>{source.publisher}</Meta>}
        <a href={source.link} target="_blank" rel="noreferrer">
          <ExternalLink size={12} /> {source.link}
        </a>
      </Name>
      <input type="file" accept=".pdf,application/pdf" aria-label={t('datasetWorkspace.uploadFile')}
        onChange={(e) => setFile(e.target.files?.[0] || null)} style={{ color: '#ccc', maxWidth: 240 }} />
      <Button $size="small" $variant="primary" disabled={!file || busy} onClick={handleUpload}>
        <Upload /> {busy ? t('datasetWorkspace.uploading') : t('datasetWorkspace.uploadFile')}
      </Button>
      {error && <StatusLine $error>{error}</StatusLine>}
    </Row>
  );
}

export default function ManualDownloads({ datasetId, sources, onChanged }) {
  const { t } = useTranslation();
  const pending = sources.filter(needsManualDownload);
  if (!pending.length) return null;

  return (
    <Section>
      <SectionTitle><ShieldAlert size={18} /> {t('datasetWorkspace.manual.title', { count: pending.length })}</SectionTitle>
      <Lead>{t('datasetWorkspace.manual.lead')}</Lead>
      <List>
        {pending.map((s) => <ManualRow key={s.id} datasetId={datasetId} source={s} onChanged={onChanged} />)}
      </List>
    </Section>
  );
}
